<script lang="ts" setup>
import { computed, ref, watch, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { directive as clickAway } from 'vue3-click-away';
import { intersection } from 'lodash';
import { useScreensizeStore } from '@stores/useScreensizeStore.ts';
import type { TypedUser } from '@models/typedUser.ts';
import type { LinkTarget } from '@global-types/linkTarget.ts';
import eventBus from '@tools/event-bus';
import { URL_USER_ORGANIZATION_TEAM } from '@tools/routes/routes';
import { slugify } from '@tools/stringFormatter.ts';
import { trans } from '@elements/translation';
import Icon from '../Icon.vue';
import UserPicture from '../UserPicture.vue';
import { QUILL_USER_MENTION_EVENT_DOWN, QUILL_USER_MENTION_EVENT_SELECT, QUILL_USER_MENTION_EVENT_UP } from './userMentionModule.ts';

const props = defineProps<{
  mention: string;
  users: TypedUser[];
}>();

defineOptions({ directives: { clickAway } });

const emit = defineEmits([ 'selectUser', 'close' ]);

const slugifiedMentions = computed<string[]>(() => props.mention.split(' ').filter((part: string) => part.length > 0).map((part: string) => slugify(part)));

const mentionableUsers = computed<TypedUser[]>(() => {
  const queries = slugifiedMentions.value;

  // If no query, initial order
  if (queries.length === 0) {
    return props.users;
  }

  // Else, filter users
  const filteredUsers: TypedUser[][] = [];
  // eslint-disable-next-line no-restricted-syntax
  for (let i = 0; i < queries.length; i++) {
    const query = queries[i];
    const filteredResultForQuery: TypedUser[] = props.users.filter((user: TypedUser) => slugify(user.email ?? '').includes(query)
      || slugify(user.firstname ?? '').includes(query)
      || slugify(user.lastname ?? '').includes(query));
    filteredUsers.push(filteredResultForQuery);
  }

  // Intersects results ("AND" rule)
  const uniqueFilteredUsers = intersection(...filteredUsers);

  // Sort alphabetically by firstname, then lastname, then email
  return uniqueFilteredUsers.sort((a: TypedUser, b: TypedUser) => {
    const fnA = a.firstname ?? '';
    const fnB = b.firstname ?? '';
    if (fnA < fnB) return -1;
    if (fnA > fnB) return 1;

    const lnA = a.lastname ?? '';
    const lnB = b.lastname ?? '';
    if (lnA < lnB) return -1;
    if (lnA > lnB) return 1;

    const emA = a.email ?? '';
    const emB = b.email ?? '';
    if (emA < emB) return -1;
    if (emA > emB) return 1;

    return 0;
  });
});

const preselectedIndex = ref<number>(0);

watch(mentionableUsers, () => {
  preselectedIndex.value = 0;
});

const selectUser = (index: number) => {
  const user = mentionableUsers.value[index];
  if (user) {
    emit('selectUser', user);
  }
};

const boxGroup = ref<HTMLDivElement | null>(null);
const SCROLL_STEP = 60;
const ITEMS_ON_VIEW = 4;

watch(preselectedIndex, (newIndex) => {
  if (mentionableUsers.value.length <= ITEMS_ON_VIEW) {
    return;
  }
  const actualScrollPx = boxGroup.value!.scrollTop;
  const actualIndexStart = Math.round(actualScrollPx / SCROLL_STEP);
  const actualIndexEnd = actualIndexStart + (ITEMS_ON_VIEW - 1);
  if (newIndex < actualIndexStart) {
    boxGroup.value!.scroll(0, (newIndex) * SCROLL_STEP);
  } else if (newIndex > actualIndexEnd) {
    boxGroup.value!.scroll(0, (newIndex - ITEMS_ON_VIEW + 1) * SCROLL_STEP);
  }
});

onMounted(() => {
  eventBus.addEventListener(QUILL_USER_MENTION_EVENT_SELECT, () => {
    selectUser(preselectedIndex.value);
  });
  eventBus.addEventListener(QUILL_USER_MENTION_EVENT_UP, () => {
    preselectedIndex.value = (mentionableUsers.value.length + preselectedIndex.value - 1) % mentionableUsers.value.length;
  });
  eventBus.addEventListener(QUILL_USER_MENTION_EVENT_DOWN, () => {
    preselectedIndex.value = (preselectedIndex.value + 1) % mentionableUsers.value.length;
  });
});

const onClickAway = () => {
  emit('close');
};

const { isMobile } = storeToRefs(useScreensizeStore());
const ctaTarget = computed<LinkTarget>(() => (isMobile.value ? '_self' : '_blank'));

const companyName = ref<string>(window.COMPANY_NAME);
</script>

<template>
  <div
    v-click-away="onClickAway"
    class="mention-box"
  >
    <div v-if="mentionableUsers.length > 0" ref="boxGroup" class="mention-box__group box-group">
      <div
        v-for="(user, index) in mentionableUsers"
        :key="user.id"
        class="box-group__item group-item"
        @click="selectUser(index)"
        @mousemove="preselectedIndex = index"
      >
        <div class="group-item__wrapper item-wrapper">
          <div class="item-wrapper__content wrapper-content">
            <div class="wrapper-content__line content-line" :class="{ 'content-line--preselected': preselectedIndex === index }">
              <UserPicture
                class="content-line__picture"
                :size="32"
                :user="user"
              />
              <div class="content-line__frame line-frame">
                <span class="line-frame__name frame-name">
                  <span class="frame-name__fullname">{{ user.firstname }} {{ user.lastname.toUpperCase() }}</span>
                  <span v-if="user.type === 'manager'" class="frame-name__company">({{ companyName }})</span>
                </span>
                <span class="line-frame__email">{{ user.email }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <a class="mention-box__cta box-cta" :href="URL_USER_ORGANIZATION_TEAM" :target="ctaTarget">
      <Icon name="ph_user-plus" :width="28" class="box-cta__icon" />
      <span class="box-cta__text">{{ trans('content_editor.mention.cta') }}</span>
    </a>
  </div>
</template>

<style lang="scss" scoped>
.mention-box {
  background-color: $colorWhite;
  position: absolute;
  border-radius: rem(8);
  box-shadow: 0 rem(4) rem(8) 0 rgba(0, 0, 0, 0.25);
  overflow: hidden;
  bottom: rem(70);
  left: rem(12);

  &__group, .box-group {
    display: inline-flex;
    flex-direction: column;
    max-height: rem(240);
    overflow-x: hidden;
    height: fit-content;
    border-bottom: 1px solid $colorGray;

    &__item, .group-item {
      display: flex;
      width: rem(316);
      min-width: rem(316);
      flex-direction: column;
      align-items: center;
      background: $colorWhite;

      &__wrapper, .item-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        align-self: stretch;
        background: $colorWhite;

        &__content, .wrapper-content {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          align-self: stretch;

          &__line, .content-line {
            display: flex;
            padding: rem(8) rem(12);
            height: rem(60);
            align-items: center;
            align-self: stretch;
            gap: rem(8);
            cursor: pointer;

            &__picture {
              width: rem(32);
              height: rem(32);
            }

            &__frame, .line-frame {
              display: flex;
              flex-direction: column;
              align-items: flex-start;
              max-width: rem(252);
              flex: 1 0 0;

              &__name, .frame-name {
                display: flex;
                align-items: center;
                max-width: 100%;

                &__fullname {
                  @include fontBodyBold;
                  color: $colorBlack;
                  white-space: nowrap;
                  max-width: 100%;
                  flex: 1;
                  min-width: 0;
                  overflow: hidden;
                  text-overflow: ellipsis;
                }

                &__company {
                  @include fontBodyBold;
                  color: $colorBlack;
                  white-space: nowrap;
                  flex-shrink: 0;
                  margin-left: rem(4);
                }
              }

              &__email {
                @include fontBodySmall;
                color: $colorGrayDarker;
                white-space: nowrap;
                text-overflow: ellipsis;
                overflow: hidden;
                max-width: 100%;
              }
            }

            &--preselected {
              background: $colorBlueLightest;

              .line-frame {
                &__name, .frame-name {
                  &__fullname, &__company {
                    color: $colorBlue;
                  }
                }

                &__email {
                  color: $colorBlue;
                }
              }
            }
          }
        }
      }
    }
  }

  &__cta, .box-cta {
    padding: rem(12) rem(18);
    display: flex;
    justify-content: start;
    gap: rem(10);
    align-items: center;
    text-decoration: none;

    &__icon {
      padding: 0 rem(4)
    }

    &__text {
      @include fontBody;
      color: $colorBlack;
      text-decoration: none;
    }

    @media (hover: hover) {
      &:hover {
        background: $colorBlueLightest;

        .box-cta {
          &__icon {
            fill: $colorBlue;
          }

          &__text {
            color: $colorBlue;
          }
        }
      }
    }
  }
}
</style>
